import { memo, useEffect, useRef, useState } from 'react';
import { Box, Skeleton } from '@mui/material';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import AudiotrackOutlinedIcon from '@mui/icons-material/AudiotrackOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { getCachedVideoThumbnail, requestVideoThumbnail } from '../../utils/videoThumbnail';

const ICONS = {
  video: PlayCircleOutlineIcon,
  image: ImageOutlinedIcon,
  audio: AudiotrackOutlinedIcon,
  application: DescriptionOutlinedIcon,
};

// jsdom and older browsers have no IntersectionObserver; treat everything as
// visible there rather than rendering empty tiles forever.
const supportsObserver = () => typeof window !== 'undefined' && typeof window.IntersectionObserver === 'function';

const useInView = (enabled) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(() => !enabled || !supportsObserver());

  useEffect(() => {
    if (inView || !enabled || !supportsObserver()) return undefined;
    const node = ref.current;
    if (!node) return undefined;

    const observer = new window.IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      // Start a row early so a scrolling user rarely sees the placeholder.
      { rootMargin: '200px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [inView, enabled]);

  return [ref, inView];
};

/**
 * Plays the hovered video over its own still frame.
 *
 * Mounted only while the card is hovered and unmounted the moment the pointer
 * leaves, which aborts the in-flight fetch - so at most one video is ever
 * streaming, and a card the user passed over costs nothing. It reuses the
 * `video_url` already in the list response, so hovering makes no API call. The
 * still frame stays painted underneath until the first frame is decoded, so the
 * tile never flashes black.
 */
const HoverVideo = ({ url, onError }) => {
  const [ready, setReady] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    // Autoplay can still be refused (a power-saving mode, a browser policy).
    // The still frame is already behind it, so a refusal just means no motion.
    const attempt = video && video.play();
    if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {});
    return () => {
      if (!video) return;
      video.pause();
      // Dropping the source is what actually cancels the download.
      video.removeAttribute('src');
      try {
        video.load();
      } catch (e) {
        /* releasing the element is best effort */
      }
    };
  }, []);

  return (
    <Box
      component="video"
      ref={videoRef}
      src={url}
      // Metadata only up front; the rest streams in as it plays and stops the
      // instant the pointer leaves.
      preload="metadata"
      muted
      loop
      playsInline
      autoPlay
      disableRemotePlayback
      tabIndex={-1}
      aria-hidden="true"
      data-testid="thumbnail-hover-video"
      onCanPlay={() => setReady(true)}
      onError={onError}
      sx={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        display: 'block',
        pointerEvents: 'none',
        opacity: ready ? 1 : 0,
        transition: 'opacity 180ms ease',
      }}
    />
  );
};

/**
 * The visual half of a media card: a 16:9 tile holding a real preview where one
 * can be produced from the URL the API already returns, and a typed icon where
 * it cannot. Nothing here loads until the tile is near the viewport, and a video
 * plays only while the pointer is resting on it.
 */
const MediaThumbnail = (props) => {
  const { file, fileType, cacheKey, lazy = true, playing = false, radius = '6px' } = props;
  const url = file?.video_url;
  const [containerRef, inView] = useInView(lazy);
  const [videoFrame, setVideoFrame] = useState(() => (cacheKey ? getCachedVideoThumbnail(cacheKey) : undefined));
  const [frameFailed, setFrameFailed] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  // A server thumbnail that 404s should cost the tile its bytes, not its picture:
  // the original is still there to fall back to before the icon is.
  const [stillFailed, setStillFailed] = useState(false);

  // The list response carries a server-made thumbnail beside the original. For
  // an 88x50 tile that is the whole file's worth of bytes saved per row, so it
  // is preferred wherever it exists - falling back to the original for the files
  // and the environments that have none. Playback and the quick preview still
  // use `video_url`; this is only what the tile paints.
  const preferredStill = file?.thumbnail_url || url;
  const stillUrl = stillFailed ? url : preferredStill;
  const [hoverPlaybackFailed, setHoverPlaybackFailed] = useState(false);

  const isVideo = fileType === 'video' && !!url;
  const isImage = fileType === 'image' && !!url;

  useEffect(() => {
    if (!isVideo || !inView || videoFrame !== undefined) return undefined;

    let cancelled = false;
    requestVideoThumbnail(url, cacheKey || url).then((result) => {
      if (!cancelled) setVideoFrame(result);
    });
    return () => {
      cancelled = true;
    };
  }, [isVideo, inView, url, cacheKey, videoFrame]);

  const FallbackIcon = ICONS[fileType] || InsertDriveFileOutlinedIcon;

  const hoverLayer =
    isVideo && playing && !hoverPlaybackFailed ? (
      <HoverVideo url={url} onError={() => setHoverPlaybackFailed(true)} />
    ) : null;

  const frame = (child) => (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        width: '100%',
        aspectRatio: '16 / 9',
        borderRadius: radius,
        overflow: 'hidden',
        backgroundColor: '#0d1a16',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {child}
      {hoverLayer}
    </Box>
  );

  if (isImage && !imageFailed) {
    return frame(
      <Box
        component="img"
        src={inView ? stillUrl : undefined}
        alt=""
        loading="lazy"
        decoding="async"
        data-testid="thumbnail-image"
        onError={() => (stillUrl !== url ? setStillFailed(true) : setImageFailed(true))}
        sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
    );
  }

  if (isVideo && !frameFailed) {
    if (videoFrame) {
      return frame(
        <Box
          component="img"
          src={videoFrame}
          alt=""
          decoding="async"
          data-testid="thumbnail-frame"
          sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      );
    }

    if (videoFrame === null) {
      // Canvas capture was refused (most often a cross-origin storage bucket).
      // The element itself still paints a frame, and `preload="metadata"` plus
      // the media fragment keeps it to a seek rather than a download.
      return frame(
        <Box
          component="video"
          src={inView ? `${url}#t=0.5` : undefined}
          preload="metadata"
          muted
          playsInline
          tabIndex={-1}
          aria-hidden="true"
          data-testid="thumbnail-video-frame"
          onError={() => setFrameFailed(true)}
          sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', pointerEvents: 'none' }}
        />
      );
    }

    return frame(<Skeleton data-testid="thumbnail-pending" variant="rectangular" animation="wave" width="100%" height="100%" sx={{ bgcolor: '#1b2b26' }} />);
  }

  return frame(<FallbackIcon data-testid="thumbnail-icon" sx={{ color: '#7e8f89', fontSize: { xs: '16px', md: '20px', lg: '24px', xl: '32px' } }} />);
};

export default memo(MediaThumbnail);
